"use client"

export const dynamic = 'force-dynamic'

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import { Check, Zap, Key, Globe, Code, Shield, ArrowRight, DollarSign, Infinity, AlertTriangle, TrendingDown } from "lucide-react"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import Link from "next/link"
import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"
import { useState } from "react"

export default function PricingPage() {
  const [isYearly, setIsYearly] = useState(false)
  const [selectedPlatform, setSelectedPlatform] = useState<'lokalise' | 'crowdin'>('crowdin')

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            <DollarSign className="w-3 h-3 mr-1" />
            Localization Without Limits, AI Without Markup
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Save 85-95% on Localization Costs
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Enterprise features without enterprise pricing. Bring your own OpenAI key and pay zero markup.
          </p>
          
          {/* Quick Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">$200-500</div>
              <div className="text-sm text-muted-foreground">Enterprise platforms</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">$22-30</div>
              <div className="text-sm text-muted-foreground">Locsy total cost</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">85-95%</div>
              <div className="text-sm text-muted-foreground">Savings</div>
            </div>
          </div>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <span className={!isYearly ? 'font-medium' : 'text-muted-foreground'}>
            Monthly
          </span>
          <Switch
            checked={isYearly}
            onCheckedChange={setIsYearly}
          />
          <span className={isYearly ? 'font-medium' : 'text-muted-foreground'}>
            Yearly
          </span>
          {isYearly && (
            <Badge variant="secondary" className="ml-2">
              Save $20 (9%)
            </Badge>
          )}
        </div>

        {/* Platform Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center rounded-lg bg-muted p-1">
            <button
              onClick={() => setSelectedPlatform('lokalise')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                selectedPlatform === 'lokalise'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              vs Lokalise
            </button>
            <button
              onClick={() => setSelectedPlatform('crowdin')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                selectedPlatform === 'crowdin'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              vs Crowdin
            </button>
          </div>
        </div>

        {/* Main Pricing Plan */}
        <div className="max-w-2xl mx-auto mb-16">
          <Card className="border-2 border-primary relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/50 to-primary"></div>
            <CardHeader className="text-center pb-8">
              <Badge variant="secondary" className="text-sm mx-auto mb-4">
                <Infinity className="w-3 h-3 mr-1" />
                Unlimited Everything Plan
              </Badge>
              <CardTitle className="text-3xl font-bold">All Features Included</CardTitle>
              <CardDescription className="text-lg">
                No hidden fees, no limits, no markup on AI costs
              </CardDescription>
              <div className="text-6xl font-bold text-primary my-4">
                ${isYearly ? '200' : '20'}
                <span className="text-2xl text-muted-foreground font-normal">
                  /{isYearly ? 'year' : 'month'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                + Your actual OpenAI costs (typically $2-10/month)
              </p>
              {isYearly && (
                <p className="text-sm text-primary font-medium">
                  Save $20 per year with annual billing (9% savings)
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">∞ Unlimited projects</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">∞ Unlimited languages</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">∞ Unlimited team members</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">∞ Unlimited file uploads</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">∞ Unlimited translations</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">∞ Unlimited reviewers</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">AI-powered translation</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">REST API & CLI access</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">Magic link reviews</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">Project-specific API keys</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">Glossary & style guides</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-sm">24/7 support</span>
                </div>
              </div>

              <div className="pt-4">
                <Link href="/auth/signup" className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 text-base h-12 px-8 w-full">
                  <Key className="w-4 h-4 mr-2" />
                  Start Free Trial
                </Link>
                <p className="text-center text-sm text-muted-foreground mt-2">
                  No credit card required • <Link href="/calculator" className="text-primary hover:underline">Calculate your savings in our demo</Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Real Platform Cost Comparison
              </h2>
              <p className="text-xl text-muted-foreground">
                Based on actual pricing from {selectedPlatform === 'lokalise' ? 'Lokalise' : 'Crowdin'}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-5 w-5 mr-2 text-orange-500" />
                  <h3 className="text-xl font-semibold">
                    {selectedPlatform === 'lokalise' ? 'Lokalise' : 'Crowdin'}
                  </h3>
                </div>
                
                {selectedPlatform === 'lokalise' ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Start plan (10 users)</span>
                      <span className="font-medium">$140/month</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Additional users (5)</span>
                      <span className="font-medium">$70/month</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Key limitations</span>
                      <span className="font-medium">5K keys</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Professional services</span>
                      <span className="font-medium">$150-300/hour</span>
                    </div>
                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center font-bold">
                        <span>Typical monthly cost</span>
                        <span className="text-orange-600 dark:text-orange-400">$210-405</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Team plan (15 users)</span>
                      <span className="font-medium">$150-200/month</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Additional users</span>
                      <span className="font-medium">$100-150/month</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Word limitations</span>
                      <span className="font-medium">Per plan tier</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Professional services</span>
                      <span className="font-medium">$100-200/hour</span>
                    </div>
                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center font-bold">
                        <span>Typical monthly cost</span>
                        <span className="text-orange-600 dark:text-orange-400">$250-350</span>
                      </div>
                    </div>
                  </div>
                )}
              </Card>

              <Card className="p-6 border-2 border-primary">
                <div className="flex items-center mb-4">
                  <DollarSign className="h-5 w-5 mr-2 text-primary" />
                  <h3 className="text-xl font-semibold">Locsy All-Inclusive</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Platform fee</span>
                    <span className="font-medium text-primary">$20/month</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Your OpenAI costs</span>
                    <span className="font-medium text-primary">$2-10/month</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Team members</span>
                    <span className="font-medium text-primary">Unlimited</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Projects & languages</span>
                    <span className="font-medium text-primary">Unlimited</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center font-bold">
                      <span>Monthly total</span>
                      <span className="text-primary">$22-30</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  *Direct OpenAI costs you pay yourself - zero markup. Estimates based on GPT-4.1-mini pricing for guidance only.
                </p>
              </Card>
            </div>

            <div className="mt-8 text-center">
              <Card className="p-6 border-2 border-primary">
                <TrendingDown className="h-5 w-5 text-primary mx-auto mb-2" />
                <p className="font-semibold">
                  Monthly savings: {selectedPlatform === 'lokalise' ? '$180-383' : '$220-328'} • Annual savings: {selectedPlatform === 'lokalise' ? '$2,160-4,596' : '$2,640-3,936'}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Save {selectedPlatform === 'lokalise' ? '85-95%' : '85-92%'} on localization costs
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why We're Different */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                How Bring Your Own Key (BYOK) Works
              </h2>
              <p className="text-xl text-muted-foreground">
                The secret to massive cost savings
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="text-center p-6 border-2 hover:border-primary/50 transition-all duration-200 hover:shadow-lg">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Key className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Your API Key</h3>
                <p className="text-muted-foreground">
                  Use your own OpenAI API key - pay OpenAI directly at cost with zero markup
                </p>
              </Card>

              <Card className="text-center p-6 border-2 hover:border-primary/50 transition-all duration-200 hover:shadow-lg">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Secure & Private</h3>
                <p className="text-muted-foreground">
                  Your API key is encrypted and never shared. Full transparency of all costs
                </p>
              </Card>

              <Card className="text-center p-6 border-2 hover:border-primary/50 transition-all duration-200 hover:shadow-lg">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Massive Savings</h3>
                <p className="text-muted-foreground">
                  Pay $0.001-0.03 per 1K tokens instead of $0.01-0.10 per word
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-muted-foreground">
                Everything you need to know about cost savings and unlimited features
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="savings" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  How much money will I save compared to enterprise platforms?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pt-4">
                  Most customers save 85-95% on their localization costs. Enterprise platforms like Lokalise charge $200-500/month for moderate usage (including additional users), while Locsy costs just $22-30/month total. For a typical team, you'd save approximately $180-470/month or $2,160-5,640 per year. <Link href="/calculator" className="text-primary hover:underline">Use our calculator to see your exact savings</Link>.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="openai-costs" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  What will my OpenAI costs actually be?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pt-4">
                  OpenAI charges about $0.15 per million input tokens and $0.60 per million output tokens for GPT-4.1-mini. For typical usage (moderate translation volume), expect $2-10 in direct OpenAI costs. Unlike enterprise platforms that may markup AI costs or charge per-word fees, you pay OpenAI directly with zero markup.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="unlimited" className="border border-border rounded-lg px-6 hover:border-primary/50 transition-colors">
                <AccordionTrigger className="text-left font-semibold hover:no-underline hover:text-primary transition-colors">
                  What does "unlimited" actually mean?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pt-4">
                  Unlimited means no artificial limits on projects, languages, team members, file uploads, translations, or API calls. Unlike enterprise platforms that charge per user or limit hosted keys (Lokalise limits to 5K-10K keys), you get full access for everyone on your team at no additional cost.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="enterprise-features" className="border border-border rounded-lg px-6 hover:border-primary/50 transition-colors">
                <AccordionTrigger className="text-left font-semibold hover:no-underline hover:text-primary transition-colors">
                  Do I get the same features as enterprise platforms?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pt-4">
                  Yes, and often more. You get AI-powered translation, REST API, CLI tools, magic link reviews, glossaries, style guides, project management, and 24/7 support. The only difference is you're not paying per-user fees or hitting hosted key limitations that enterprise platforms impose.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="migration" className="border border-border rounded-lg px-6 hover:border-primary/50 transition-colors">
                <AccordionTrigger className="text-left font-semibold hover:no-underline hover:text-primary transition-colors">
                  How easy is it to migrate from platforms like Lokalise or Crowdin?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pt-4">
                  Very easy. We support all standard file formats and provide migration tools to import your existing translations, glossaries, and projects. Most teams complete migration in 1-2 days and start saving $180-470/month immediately. We offer free migration assistance.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="hidden-costs" className="border border-border rounded-lg px-6 hover:border-primary/50 transition-colors">
                <AccordionTrigger className="text-left font-semibold hover:no-underline hover:text-primary transition-colors">
                  Are there any hidden costs or limits I should know about?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pt-4">
                  No hidden costs. The only variable cost is your OpenAI usage, which you control and pay directly. No setup fees, no per-user charges, no hosted key limits, no volume restrictions, and no surprise invoices. What you see is what you pay.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Ready to Save 85-95% on Localization?</h2>
            <p className="text-muted-foreground mb-8">
              Join teams who've already saved thousands by switching from enterprise platforms
            </p>
            <Link href="/auth/signup" className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 text-base h-12 px-8">
              Start Free Trial - No Credit Card
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <p className="text-sm text-muted-foreground mt-4">
              14-day free trial • Cancel anytime • Free migration assistance
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
} 